import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import playerService from '../../services/playerservice';
import './PlayerList.css';

function PlayerList() {
  const [allPlayers, setAllPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [sortOrder] = useState('asc'); // Mantemos apenas o valor, sem warnings

  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        // Solicitando ao backend a lista completa de jogadores de uma vez só
        const response = await playerService.getAll({ paginate: 'false', unpaginated: 'true' });
        
        const playersArray = response.data && Array.isArray(response.data.data)
          ? response.data.data
          : (Array.isArray(response.data) ? response.data : []);

        setAllPlayers(playersArray);
      } catch (err) {
        console.error(err);
        setError('Não foi possível carregar os jogadores.');
      } finally {
        setLoading(false);
      }
    };
    fetchPlayers();
  }, []);

  const uniqueTeams = useMemo(() => {
    const map = new Map();
    if (Array.isArray(allPlayers)) {
      allPlayers.forEach(p => {
        if (p.team?._id) map.set(p.team._id, { _id: p.team._id, name: p.team.name });
      });
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [allPlayers]);

  // ✅ CORRIGIDO: Substituída a referência errada 'cleanCpf' por 'cleanSearch'
  const filteredPlayers = useMemo(() => {
    if (!Array.isArray(allPlayers)) return [];
    
    let players = [...allPlayers];
    
    if (searchTerm) {
      const cleanSearch = searchTerm.replace(/\D/g, ''); // Limpa caracteres especiais se for CPF
      
      players = players.filter(p => {
        // 1. Busca por nome parcial
        const nameMatch = p.name.toLowerCase().includes(searchTerm.toLowerCase());
        
        // 2. Busca por apelido parcial
        const nicknameMatch = p.nickname && p.nickname.toLowerCase().includes(searchTerm.toLowerCase());
        
        // 3. Busca por CPF parcial (ignora pontos e traços)
        const playerCpfClean = p.cpf ? p.cpf.replace(/\D/g, '') : '';
        const cpfMatch = cleanSearch && playerCpfClean.includes(cleanSearch);

        return nameMatch || nicknameMatch || cpfMatch;
      });
    }
    
    if (selectedTeam) {
      players = players.filter(p => p.team?._id === selectedTeam);
    }
    
    return players.sort((a, b) => sortOrder === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name));
  }, [allPlayers, searchTerm, selectedTeam, sortOrder]);

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este jogador?')) return;
    try {
      await playerService.deletePlayer(id);
      setAllPlayers(prev => prev.filter(p => p._id !== id));
    } catch (err) {
      console.error(err);
      alert('Falha ao excluir jogador.');
    }
  };

  if (loading) return <div className="loading-state">Carregando jogadores...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="player-list-container">
      <div className="header">
        <h2>Jogadores</h2>
        <Link to="/players/new" className="new-team-button">Novo Jogador</Link>
      </div>

      <div className="search-container">
        <input
          type="text"
          placeholder="Buscar por nome, apelido ou CPF..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          value={selectedTeam}
          onChange={(e) => setSelectedTeam(e.target.value)}
          className="filter-select"
        >
          <option value="">Todos os Times</option>
          {uniqueTeams.map(team => (
            <option key={team._id} value={team._id}>{team.name}</option>
          ))}
        </select>
      </div>

      <div className="player-grid">
        {filteredPlayers.length ? (
          filteredPlayers.map(player => (
            <div key={player._id} className="player-card">
              <div className="player-card-header">
                <div className="player-photo-container">
                  <img
                    src={playerService.getPlayerPhotoUrl(player.photo)}
                    alt={player.name}
                    className="player-photo"
                    onError={(e) => { e.target.onerror = null; e.target.src = '/default-player-photo.png'; }}
                  />
                </div>
                <div className="player-info">
                  <h3>{player.name}</h3>
                  <p>Posição: {player.position || 'Não informada'}</p>
                  {player.team && <p>Time: {player.team.name}</p>}
                  {player.cpf && (
                    <p style={{ fontSize: '11px', color: '#6b7280' }}>
                      CPF: {player.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')}
                    </p>
                  )}
                </div>
              </div>

              <div className="player-actions">
                <Link to={`/players/${player._id}`} className="button details-button">Detalhes</Link>
                <Link to={`/players/${player._id}/edit`} className="button edit-button">Editar</Link>
                <button onClick={() => handleDelete(player._id)} className="button delete-button">Excluir</button>
              </div>
            </div>
          ))
        ) : (
          <div className="no-players-found">
            <p>Nenhum jogador encontrado</p>
            <Link to="/players/new">Cadastre um novo jogador</Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default PlayerList;